import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Receipt as InvoiceIcon,
  Visibility as DetailsIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateIcon,
  Inventory as QuantityIcon,
} from "@mui/icons-material";
import { apiCall } from "../../services/ApiCall.js";
import config from "../../config/config.json";

const InvoiceTable = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Original state variables (unchanged)
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');

  const primaryColor = "#0d6efd";

  // Original API function (unchanged logic)
  const getAllInvoice = async () => {
    setLoading(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getAllInvoice`,
        {},
        {},
        "GET"
      );
      if (isSuccess) {
        setData(data?.data?.reverse() || []);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setData([]);
    }
    setLoading(false);
  };

  // Original useEffect (unchanged logic)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else {
      getAllInvoice();
    }
  }, []);

  // Original filtering logic (unchanged)
  const filteredData = data.filter(item =>
    Object.values(item).some(val =>
      val && val.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Original date formatting function (unchanged)
  function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  // Original pagination logic (unchanged)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle page change for Material-UI pagination
  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const LoadingSkeleton = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Zoho Invoice ID</TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Invoice Created Date</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(5)].map((_, index) => (
            <TableRow key={index}>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
              <TableCell><Skeleton /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'transparent' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: primaryColor, mb: 1 }}>
              Invoices
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and view invoice records
            </Typography>
          </Box>
        </Box>

        {/* Controls Section */}
        <Grid container spacing={2} alignItems="center">
          {/* Show Entries */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Show
              </Typography>
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={250}>250</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                entries
              </Typography>
            </Box>
          </Grid>

          {/* Search Box */}
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>

          {/* Filter and Results Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
              <IconButton
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: `${primaryColor}10`,
                    borderColor: primaryColor,
                  },
                }}
              >
                <FilterIcon />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                {filteredData.length} invoices
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: `${primaryColor}08` }}>
                    <TableCell sx={{ fontWeight: 600, color: primaryColor }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InvoiceIcon fontSize="small" />
                        Zoho Invoice ID
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: primaryColor }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QuantityIcon fontSize="small" />
                        Quantity
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: primaryColor }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyIcon fontSize="small" />
                        Amount
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: primaryColor }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DateIcon fontSize="small" />
                        Invoice Created Date
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: primaryColor, textAlign: 'center' }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentItems && currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': {
                            backgroundColor: `${primaryColor}05`,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: primaryColor }}>
                            {item.invoiceId || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.invoiceAmount ? Math.round(Number(item.invoiceAmount) / 95) : 0}
                            size="small"
                            sx={{
                              backgroundColor: `${primaryColor}15`,
                              color: primaryColor,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MoneyIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                              ${item.invoiceAmount || '0'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(item.invoiceRaiseDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Button
                            component={Link}
                            to={`/invoice/${item.invoiceId}`}
                            variant="outlined"
                            size="small"
                            startIcon={<DetailsIcon />}
                            sx={{
                              borderColor: primaryColor,
                              color: primaryColor,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              '&:hover': {
                                backgroundColor: `${primaryColor}10`,
                                borderColor: primaryColor,
                              }
                            }}
                          >
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2
                        }}>
                          <InvoiceIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                          <Typography variant="h6" color="text.secondary">
                            No invoices found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm ? 'Try adjusting your search criteria' : 'No invoice data available'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredData.length}
              rowsPerPage={itemsPerPage}
              page={currentPage - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[50, 100, 250, 500]}
              sx={{
                borderTop: '1px solid #ddd',
                backgroundColor: '#fff',
              }}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default InvoiceTable;